import * as THREE from 'three';
import { CarModel } from './CarModel';
import { ShoulderCheckController } from './ShoulderCheckController';

export type CameraMode = 'cockpit' | 'cockpit-driver' | 'cockpit-dash' | 'cockpit-hood' | 'chase' | 'topdown';

export interface DriverSettings {
  seatHeightOffset: number;   // metres, -0.15 to +0.15
  seatDepthOffset: number;    // metres, -0.25 to +0.15 (forward/back)
  mirrorLeftAngle: number;    // degrees, -20 to +20 extra yaw
  mirrorRightAngle: number;   // degrees, -20 to +20 extra yaw
  mirrorInteriorAngle: number;// degrees, -10 to +10 tilt
}

export const DEFAULT_DRIVER_SETTINGS: DriverSettings = {
  seatHeightOffset: 0,
  seatDepthOffset: 0,
  mirrorLeftAngle: 0,
  mirrorRightAngle: 0,
  mirrorInteriorAngle: 0,
};

export class MirrorsAndCameras {
  public mainCamera: THREE.PerspectiveCamera;
  public cameraMode: CameraMode = 'cockpit-driver';
  public pipMirrorsEnabled: boolean = true;

  public shoulderCheck: ShoulderCheckController;

  // Driver seat & mirror settings
  public driverSettings: DriverSettings = { ...DEFAULT_DRIVER_SETTINGS };

  // Mirror cameras & render targets
  private interiorRenderTarget!: THREE.WebGLRenderTarget;
  private leftRenderTarget!: THREE.WebGLRenderTarget;
  private rightRenderTarget!: THREE.WebGLRenderTarget;

  public interiorCamera!: THREE.PerspectiveCamera;
  public leftCamera!: THREE.PerspectiveCamera;
  public rightCamera!: THREE.PerspectiveCamera;

  // Camera smoothing vectors
  private chasePosition = new THREE.Vector3();
  private chaseLookAt = new THREE.Vector3();

  // Camera trauma / impact shake intensity (0.0 to 1.0)
  public cameraTrauma: number = 0;

  public addTrauma(amount: number) {
    this.cameraTrauma = Math.min(1.0, this.cameraTrauma + Math.max(0, amount));
  }


  // Driver eye position in vehicle local space (Left Hand Drive — German StVO)
  // X: +0.38m  — Left side of vehicle, aligned behind steering wheel (X=+0.38)
  // Y:  1.10m  — Calibrated seated eye height matching eSim screen (dials in lower 30%, road clearly visible ahead)
  // Z: +0.05m  — Positioned comfortably behind steering wheel (z=0.30) with horizon centered at 50%
  public readonly driverEyeBaseOffset = new THREE.Vector3(0.38, 1.10, 0.05);



  constructor(mainCamera: THREE.PerspectiveCamera) {
    this.mainCamera = mainCamera;
    this.shoulderCheck = new ShoulderCheckController();
    this.loadSettings();
    this.setupMirrors();
  }

  public get driverEyeOffset(): THREE.Vector3 {
    return new THREE.Vector3(
      this.driverEyeBaseOffset.x,
      this.driverEyeBaseOffset.y + this.driverSettings.seatHeightOffset,
      this.driverEyeBaseOffset.z + this.driverSettings.seatDepthOffset,
    );
  }

  public loadSettings() {
    try {
      if (typeof localStorage === 'undefined') return;
      const saved = localStorage.getItem('fahrschule_driver_settings');
      if (saved) {
        this.driverSettings = { ...DEFAULT_DRIVER_SETTINGS, ...JSON.parse(saved) };
      }
    } catch { /* ignore */ }
  }

  public saveSettings() {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('fahrschule_driver_settings', JSON.stringify(this.driverSettings));
      }
    } catch { /* ignore */ }
  }

  public setDriverSetting(key: keyof DriverSettings, value: number) {
    this.driverSettings[key] = value;
    this.saveSettings();
  }

  public resetDriverSettings() {
    this.driverSettings = { ...DEFAULT_DRIVER_SETTINGS };
    this.saveSettings();
  }



  private setupMirrors() {
    // 1. Interior Rearview Mirror (Aspect ratio calibrated to 23.2cm x 6.4cm glass = 3.62)
    this.interiorRenderTarget = new THREE.WebGLRenderTarget(768, 212, {
      generateMipmaps: false,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
    });
    this.interiorCamera = new THREE.PerspectiveCamera(34, 768 / 212, 0.1, 180);

    // 2. Left Side Mirror (Aspect ratio calibrated to 13cm x 7.8cm glass = 1.66)
    this.leftRenderTarget = new THREE.WebGLRenderTarget(256, 154, {
      generateMipmaps: false,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
    });
    this.leftCamera = new THREE.PerspectiveCamera(34, 256 / 154, 0.1, 150);

    // 3. Right Side Mirror
    this.rightRenderTarget = new THREE.WebGLRenderTarget(256, 154, {
      generateMipmaps: false,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
    });
    this.rightCamera = new THREE.PerspectiveCamera(34, 256 / 154, 0.1, 150);
  }

  public bindMirrorMaterials(carModel: CarModel) {
    const intMesh = carModel.cockpit.interiorMirrorMesh;
    if (intMesh) {
      intMesh.material = new THREE.MeshBasicMaterial({
        map: this.interiorRenderTarget.texture,
        side: THREE.DoubleSide,
      });
    }

    const leftMesh = carModel.cockpit.leftMirrorMesh;
    if (leftMesh) {
      leftMesh.material = new THREE.MeshBasicMaterial({
        map: this.leftRenderTarget.texture,
        side: THREE.DoubleSide,
      });
    }

    const rightMesh = carModel.cockpit.rightMirrorMesh;
    if (rightMesh) {
      rightMesh.material = new THREE.MeshBasicMaterial({
        map: this.rightRenderTarget.texture,
        side: THREE.DoubleSide,
      });
    }
  }

  public update(deltaTime: number, carModel: CarModel) {
    this.shoulderCheck.update(deltaTime);

    const carPos = carModel.mesh.position;
    const carQuat = carModel.mesh.quaternion;

    // Decay collision trauma shake
    if (this.cameraTrauma > 0.001) {
      this.cameraTrauma = Math.max(0, this.cameraTrauma - deltaTime * 2.6);
    } else {
      this.cameraTrauma = 0;
    }

    // 1. Update Main Camera based on active Mode
    const isCockpit = this.cameraMode === 'cockpit' || this.cameraMode === 'cockpit-driver' || this.cameraMode === 'cockpit-dash' || this.cameraMode === 'cockpit-hood';

    if (isCockpit) {
      const isExternalFront = this.cameraMode === 'cockpit-hood' || this.cameraMode === 'cockpit-dash';
      if (carModel.roofMesh) carModel.roofMesh.visible = !isExternalFront;
      if (carModel.signGroup) carModel.signGroup.visible = false;
      if (carModel.windowsGroup) carModel.windowsGroup.visible = !isExternalFront;
      if (carModel.pillarsGroup) carModel.pillarsGroup.visible = !isExternalFront;
      if (carModel.cockpit && carModel.cockpit.group) carModel.cockpit.group.visible = !isExternalFront;
      if (carModel.cockpit && carModel.cockpit.mirrorGroup) carModel.cockpit.mirrorGroup.visible = !isExternalFront;
      if (carModel.cockpit && carModel.cockpit.interiorMirrorGroup) carModel.cockpit.interiorMirrorGroup.visible = !this.pipMirrorsEnabled && !isExternalFront;
      if (carModel.cockpit && carModel.cockpit.roofHeadlinerGroup) carModel.cockpit.roofHeadlinerGroup.visible = !isExternalFront;
      this.updateCockpitCamera(carModel);
    } else {
      if (carModel.roofMesh) carModel.roofMesh.visible = true;
      if (carModel.signGroup) carModel.signGroup.visible = true;
      if (carModel.windowsGroup) carModel.windowsGroup.visible = true;
      if (carModel.pillarsGroup) carModel.pillarsGroup.visible = true;
      // In chase & exterior views, hide interior seats/dash, but keep exterior side mirrors visible on the car doors
      if (carModel.cockpit && carModel.cockpit.group) carModel.cockpit.group.visible = false;
      if (carModel.cockpit && carModel.cockpit.mirrorGroup) carModel.cockpit.mirrorGroup.visible = true;
      if (carModel.cockpit && carModel.cockpit.interiorMirrorGroup) carModel.cockpit.interiorMirrorGroup.visible = false;
      if (carModel.cockpit && carModel.cockpit.roofHeadlinerGroup) carModel.cockpit.roofHeadlinerGroup.visible = false;

      if (this.cameraMode === 'chase') {
        this.updateChaseCamera(deltaTime, carPos, carQuat);
      } else if (this.cameraMode === 'topdown') {
        this.updateTopDownCamera(deltaTime, carPos, carQuat);
      }
    }

    // Apply trauma shake to main camera (cockpit and chase views)
    if (this.cameraTrauma > 0.005) {
      const shakeMag = this.cameraTrauma * this.cameraTrauma; // quadratic response
      const pJitter = new THREE.Vector3(
        (Math.random() - 0.5) * 0.14 * shakeMag,
        (Math.random() - 0.5) * 0.10 * shakeMag,
        (Math.random() - 0.5) * 0.14 * shakeMag
      );
      this.mainCamera.position.add(pJitter);

      this.mainCamera.rotation.x += (Math.random() - 0.5) * 0.08 * shakeMag;
      this.mainCamera.rotation.y += (Math.random() - 0.5) * 0.08 * shakeMag;
      this.mainCamera.rotation.z += (Math.random() - 0.5) * 0.05 * shakeMag;
    }

    // 2. Update Mirror Camera transforms
    this.updateMirrorCameras(carModel);
  }


  // ─── HTML Canvas Mirror Overlays ──────────────────────────────────────────
  // Rendered from WebGL render targets → painted to HTML canvases each frame
  private mirrorContainer!: HTMLDivElement;
  private interiorCanvas!: HTMLCanvasElement;
  private leftCanvas!: HTMLCanvasElement;
  private rightCanvas!: HTMLCanvasElement;
  private interiorCtx!: CanvasRenderingContext2D;
  private leftCtx!: CanvasRenderingContext2D;
  private rightCtx!: CanvasRenderingContext2D;

  private intFrame!: HTMLElement;
  private leftFrame!: HTMLElement;
  private rightFrame!: HTMLElement;

  public getMirrorScale(w: number, h: number): number {
    const aspect = h > 0 ? w / h : 1.77;
    const isMobile = w < 768 || aspect < 1.35;
    return isMobile ? Math.min(1.0, Math.max(0.55, w / 750)) : 1.0;
  }

  public mountMirrorOverlays(root: HTMLElement) {
    this.mirrorContainer = document.createElement('div');
    this.mirrorContainer.id = 'mirror-overlays-container';
    this.mirrorContainer.style.cssText = `
      position: absolute; top: 0; left: 0; width: 100%; height: 100%;
      pointer-events: none; z-index: 12;
    `;

    // ── Interior rearview mirror frame ────────────────────────────────────
    this.intFrame = document.createElement('div');
    this.intFrame.style.cssText = `
      position: absolute; top: 10px;
      border: 3px solid #334155; border-radius: 6px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.7);
      overflow: hidden; width: 280px; height: 88px;
      pointer-events: none; background: transparent;
      transition: width 0.2s, height 0.2s, left 0.2s;
    `;
    this.mirrorContainer.appendChild(this.intFrame);

    // ── Left wing mirror frame ────────────────────────────────────────────
    this.leftFrame = document.createElement('div');
    this.leftFrame.style.cssText = `
      position: absolute; bottom: 120px; left: 16px;
      border: 3px solid #334155; border-radius: 6px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.7);
      overflow: hidden; width: 150px; height: 106px;
      pointer-events: none; background: transparent;
      transition: width 0.2s, height 0.2s, bottom 0.2s, left 0.2s;
    `;
    this.mirrorContainer.appendChild(this.leftFrame);

    // ── Right wing mirror frame ───────────────────────────────────────────
    this.rightFrame = document.createElement('div');
    this.rightFrame.style.cssText = `
      position: absolute; bottom: 120px; right: 16px;
      border: 3px solid #334155; border-radius: 6px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.7);
      overflow: hidden; width: 150px; height: 106px;
      pointer-events: none; background: transparent;
      transition: width 0.2s, height 0.2s, bottom 0.2s, right 0.2s;
    `;
    this.mirrorContainer.appendChild(this.rightFrame);

    root.appendChild(this.mirrorContainer);
    this.updateMirrorOverlayFrames();

    window.addEventListener('resize', () => this.updateMirrorOverlayFrames());
  }

  public updateMirrorOverlayFrames() {
    if (!this.mirrorContainer || !this.intFrame || !this.leftFrame || !this.rightFrame) return;
    const w = window.innerWidth;
    const h = window.innerHeight;
    const aspect = w / h;
    const isMobile = w < 768 || aspect < 1.35 || (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0 && w < 1024);
    const scale = this.getMirrorScale(w, h);

    const intW = Math.round(280 * scale);
    const intH = Math.round(88 * scale);
    const intX = Math.round((w - intW) / 2);
    const topMargin = isMobile ? 6 : 10;
    this.intFrame.style.width = `${intW}px`;
    this.intFrame.style.height = `${intH}px`;
    this.intFrame.style.left = `${intX}px`;
    this.intFrame.style.transform = 'none';
    this.intFrame.style.top = `${topMargin}px`;

    const leftW = Math.round(150 * scale);
    const leftH = Math.round(106 * scale);
    this.leftFrame.style.width = `${leftW}px`;
    this.leftFrame.style.height = `${leftH}px`;
    if (isMobile) {
      this.leftFrame.style.top = '48px';
      this.leftFrame.style.bottom = 'auto';
      this.leftFrame.style.left = '8px';
    } else {
      this.leftFrame.style.top = 'auto';
      this.leftFrame.style.bottom = '120px';
      this.leftFrame.style.left = '16px';
    }

    const rightW = Math.round(150 * scale);
    const rightH = Math.round(106 * scale);
    this.rightFrame.style.width = `${rightW}px`;
    this.rightFrame.style.height = `${rightH}px`;
    if (isMobile) {
      this.rightFrame.style.top = '48px';
      this.rightFrame.style.bottom = 'auto';
      this.rightFrame.style.right = '8px';
    } else {
      this.rightFrame.style.top = 'auto';
      this.rightFrame.style.bottom = '120px';
      this.rightFrame.style.right = '16px';
    }
  }

  public setMirrorOverlaysVisible(visible: boolean) {
    if (this.mirrorContainer) {
      this.mirrorContainer.style.display = (visible && this.pipMirrorsEnabled) ? 'block' : 'none';
    }
  }

  /**
   * GPU-accelerated Picture-in-Picture mirror rendering.
   * Renders sub-viewports directly onto the main canvas in all camera modes with zero CPU readback or memory stalls.
   */
  public renderMirrorViewports(renderer: THREE.WebGLRenderer, scene: THREE.Scene, carModel?: CarModel) {
    if (!this.pipMirrorsEnabled) return;

    const pr = renderer.getPixelRatio();
    const w = window.innerWidth;
    const h = window.innerHeight;
    const aspect = w / h;
    const isMobile = w < 768 || aspect < 1.35 || (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0 && w < 1024);
    const scale = this.getMirrorScale(w, h);

    renderer.autoClear = false;
    renderer.setScissorTest(true);

    const prevShadowAuto = renderer.shadowMap.autoUpdate;
    renderer.shadowMap.autoUpdate = false; // Disable heavy shadow map passes for mirrors

    const mirrorGroupVisible = carModel?.cockpit?.mirrorGroup?.visible ?? false;
    if (carModel?.cockpit?.mirrorGroup) {
      carModel.cockpit.mirrorGroup.visible = false;
    }
    const windowsVisible = carModel?.windowsGroup?.visible ?? false;
    if (carModel?.windowsGroup) {
      carModel.windowsGroup.visible = false;
    }

    try {
      // 1. Interior Mirror (centered horizontally at the top of the windshield / screen)
      const intW = Math.round(280 * scale);
      const intH = Math.round(88 * scale);
      const intX = Math.round((w - intW) / 2);
      const topMargin = isMobile ? 6 : 10;
      const intY = Math.round(h - topMargin - intH);

      renderer.setViewport(intX * pr, intY * pr, intW * pr, intH * pr);
      renderer.setScissor(intX * pr, intY * pr, intW * pr, intH * pr);
      renderer.clearDepth();
      renderer.render(scene, this.interiorCamera);

      // 2. Left Wing Mirror
      const leftW = Math.round(150 * scale);
      const leftH = Math.round(106 * scale);
      const leftX = isMobile ? 8 : 16;
      const bottomMargin = 120;
      const leftY = isMobile ? Math.round(h - 48 - leftH) : bottomMargin;

      renderer.setViewport(leftX * pr, leftY * pr, leftW * pr, leftH * pr);
      renderer.setScissor(leftX * pr, leftY * pr, leftW * pr, leftH * pr);
      renderer.clearDepth();
      renderer.render(scene, this.leftCamera);

      // 3. Right Wing Mirror
      const rightW = Math.round(150 * scale);
      const rightH = Math.round(106 * scale);
      const rightX = Math.round(w - (isMobile ? 8 : 16) - rightW);
      const rightY = isMobile ? Math.round(h - 48 - rightH) : bottomMargin;

      renderer.setViewport(rightX * pr, rightY * pr, rightW * pr, rightH * pr);
      renderer.setScissor(rightX * pr, rightY * pr, rightW * pr, rightH * pr);
      renderer.clearDepth();
      renderer.render(scene, this.rightCamera);
    } finally {
      // Restore full canvas viewport & scissor
      renderer.setScissorTest(false);
      renderer.setViewport(0, 0, w * pr, h * pr);
      renderer.shadowMap.autoUpdate = prevShadowAuto;
      renderer.autoClear = true;
      if (carModel?.windowsGroup) {
        carModel.windowsGroup.visible = windowsVisible;
      }
      if (carModel?.cockpit?.mirrorGroup) {
        carModel.cockpit.mirrorGroup.visible = mirrorGroupVisible;
      }
    }
  }

  /**
   * Backwards-compatible no-op (CPU readback removed for 60+ FPS performance).
   */
  public paintMirrorCanvases(_renderer: THREE.WebGLRenderer) {}

  private updateCockpitCamera(carModel: CarModel) {
    const carPos = carModel.mesh.position;
    const carQuat = carModel.mesh.quaternion;

    const aspect = typeof window !== 'undefined' && window.innerHeight > 0
      ? window.innerWidth / window.innerHeight
      : 1.77;
    const isMobileAspect = aspect < 1.35;

    const eyeOffset = this.driverEyeOffset.clone();

    if (this.cameraMode === 'cockpit-dash') {
      // ── Motorhaube (Windshield Cowl Base looking forward down over hatchback hood) ──
      eyeOffset.x = 0;
      eyeOffset.y = 0.94 + this.driverSettings.seatHeightOffset;
      eyeOffset.z = 0.72 + this.driverSettings.seatDepthOffset;
    } else if (this.cameraMode === 'cockpit-hood') {
      // ── Stoßstange (Front Bumper road-level view) ──
      eyeOffset.x = 0;
      eyeOffset.y = 0.58 + this.driverSettings.seatHeightOffset;
      eyeOffset.z = 1.95 + this.driverSettings.seatDepthOffset;
    } else {
      // ── Fahrersitz (City Car Driving / Classic Cockpit — German LHD) ──
      // Calibrated to give clear road & zebra crossing visibility while framing instruments in lower third:
      // - Eye position aligns behind steering wheel with commanding view over hood
      eyeOffset.x = this.driverEyeBaseOffset.x;
      eyeOffset.y = this.driverEyeBaseOffset.y + this.driverSettings.seatHeightOffset;
      eyeOffset.z = this.driverEyeBaseOffset.z + this.driverSettings.seatDepthOffset;

      if (isMobileAspect) {
        eyeOffset.z -= 0.06;
        eyeOffset.y += 0.02;
      }
    }

    // Calculate eye world position (includes seat height/depth offsets)
    const eyeWorldPos = eyeOffset.applyQuaternion(carQuat).add(carPos);
    this.mainCamera.position.copy(eyeWorldPos);

    let basePitch = 0.0;
    if (this.cameraMode === 'cockpit-dash') {
      basePitch = 0.05; // look forward down over the hood to see bonnet contours and road
    } else if (this.cameraMode === 'cockpit-hood') {
      basePitch = 0.01; // straight ahead along asphalt
    } else {
      const seatHeightNorm = this.driverSettings.seatHeightOffset / 0.15;
      basePitch = (isMobileAspect ? 0.01 : 0.00) + seatHeightNorm * 0.02;
    }

    const euler = new THREE.Euler().setFromQuaternion(carQuat, 'YXZ');
    euler.y += this.shoulderCheck.lookAngleY;
    euler.x += this.shoulderCheck.lookPitchX + basePitch;

    const lookQuat = new THREE.Quaternion().setFromEuler(euler);
    const forwardVec = new THREE.Vector3(0, 0, 10).applyQuaternion(lookQuat);
    this.mainCamera.lookAt(eyeWorldPos.clone().add(forwardVec));
  }

  public setDriverPreset(preset: 'citycardriving' | 'fahrsimulator' | 'hood' | 'sport' | 'default') {
    if (preset === 'citycardriving') {
      this.driverSettings.seatHeightOffset = 0.0;
      this.driverSettings.seatDepthOffset = 0.0;
      this.cameraMode = 'cockpit-driver';
    } else if (preset === 'fahrsimulator') {
      this.driverSettings.seatHeightOffset = 0.04;
      this.driverSettings.seatDepthOffset = 0.06;
      this.cameraMode = 'cockpit-dash';
    } else if (preset === 'hood') {
      this.driverSettings.seatHeightOffset = 0.0;
      this.driverSettings.seatDepthOffset = 0.0;
      this.cameraMode = 'cockpit-hood';
    } else if (preset === 'sport') {
      this.driverSettings.seatHeightOffset = -0.06;
      this.driverSettings.seatDepthOffset = -0.05;
      this.cameraMode = 'cockpit-driver';
    } else {
      this.resetDriverSettings();
      this.cameraMode = 'cockpit-driver';
    }
  }


  private updateChaseCamera(deltaTime: number, carPos: THREE.Vector3, carQuat: THREE.Quaternion) {
    const behindOffset = new THREE.Vector3(0, 2.3, -6.6).applyQuaternion(carQuat);
    const targetPos = carPos.clone().add(behindOffset);

    this.chasePosition.lerp(targetPos, Math.min(1.0, deltaTime * 10));
    this.mainCamera.position.copy(this.chasePosition);

    const lookOffset = new THREE.Vector3(0, 0.9, 1.2).applyQuaternion(carQuat);
    const targetLookAt = carPos.clone().add(lookOffset);
    this.chaseLookAt.lerp(targetLookAt, Math.min(1.0, deltaTime * 12));

    this.mainCamera.lookAt(this.chaseLookAt);
  }

  private updateTopDownCamera(deltaTime: number, carPos: THREE.Vector3, carQuat: THREE.Quaternion) {
    const topPos = carPos.clone().add(new THREE.Vector3(0, 14, 0));
    this.mainCamera.position.lerp(topPos, Math.min(1.0, deltaTime * 12));

    const forwardVec = new THREE.Vector3(0, 0, 1).applyQuaternion(carQuat);
    this.mainCamera.up.copy(forwardVec);
    this.mainCamera.lookAt(carPos);
  }

  private updateMirrorCameras(carModel: CarModel) {
    const carPos = carModel.mesh.position;
    const carQuat = carModel.mesh.quaternion;

    const ds = this.driverSettings;

    // 1. Interior Mirror Camera (Innenspiegel) — positioned along rear glass sightline for crystal-clear rear road view
    const intPos = new THREE.Vector3(0.08, 1.24, -0.20).applyQuaternion(carQuat).add(carPos);
    this.interiorCamera.position.copy(intPos);
    const intTiltRad = THREE.MathUtils.degToRad(ds.mirrorInteriorAngle);
    const intLookBase = new THREE.Vector3(0, 1.12 + Math.sin(intTiltRad) * 2, -15);
    const intLook = intLookBase.applyQuaternion(carQuat).add(carPos);
    this.interiorCamera.lookAt(intLook);

    // 2. Left Side Mirror (Linker Außenspiegel — Driver exterior door sill = +0.82)
    const leftPos = new THREE.Vector3(0.82, 0.94, 0.60).applyQuaternion(carQuat).add(carPos);
    this.leftCamera.position.copy(leftPos);
    const leftAngleRad = THREE.MathUtils.degToRad(ds.mirrorLeftAngle);
    const leftLookBase = new THREE.Vector3(0.95 + Math.sin(leftAngleRad) * 10, 0.88, -15);
    const leftLook = leftLookBase.applyQuaternion(carQuat).add(carPos);
    this.leftCamera.lookAt(leftLook);

    // 3. Right Side Mirror (Rechter Außenspiegel — Passenger exterior door sill = -0.82)
    const rightPos = new THREE.Vector3(-0.82, 0.94, 0.60).applyQuaternion(carQuat).add(carPos);
    this.rightCamera.position.copy(rightPos);
    const rightAngleRad = THREE.MathUtils.degToRad(ds.mirrorRightAngle);
    const rightLookBase = new THREE.Vector3(-0.95 + Math.sin(rightAngleRad) * 10, 0.88, -15);
    const rightLook = rightLookBase.applyQuaternion(carQuat).add(carPos);
    this.rightCamera.lookAt(rightLook);
  }

  public renderMirrors(renderer: THREE.WebGLRenderer, scene: THREE.Scene, carModel: CarModel) {
    // Temporarily hide cockpit mirrorGroup so that mirror textures are NOT sampled while being rendered into (prevents WebGL feedback loop error)
    const originalTarget = renderer.getRenderTarget();
    const prevShadowAuto = renderer.shadowMap.autoUpdate;
    renderer.shadowMap.autoUpdate = false;

    const mirrorGroupVisible = carModel.cockpit?.mirrorGroup?.visible ?? false;
    if (carModel.cockpit?.mirrorGroup) {
      carModel.cockpit.mirrorGroup.visible = false;
    }

    // Also temporarily hide tinted window glass so mirror cameras have crystal-clear rear sightlines
    const windowsVisible = carModel.windowsGroup?.visible ?? false;
    if (carModel.windowsGroup) {
      carModel.windowsGroup.visible = false;
    }

    try {
      // 1. Interior
      renderer.setRenderTarget(this.interiorRenderTarget);
      renderer.render(scene, this.interiorCamera);

      // 2. Left
      renderer.setRenderTarget(this.leftRenderTarget);
      renderer.render(scene, this.leftCamera);

      // 3. Right
      renderer.setRenderTarget(this.rightRenderTarget);
      renderer.render(scene, this.rightCamera);
    } finally {
      renderer.setRenderTarget(originalTarget);
      renderer.shadowMap.autoUpdate = prevShadowAuto;
      if (carModel.windowsGroup) {
        carModel.windowsGroup.visible = windowsVisible;
      }
      if (carModel.cockpit?.mirrorGroup) {
        carModel.cockpit.mirrorGroup.visible = mirrorGroupVisible;
      }
    }
  }

  public toggleCameraMode() {
    if (this.cameraMode === 'cockpit' || this.cameraMode === 'cockpit-driver') {
      this.cameraMode = 'cockpit-dash';
    } else if (this.cameraMode === 'cockpit-dash') {
      this.cameraMode = 'cockpit-hood';
    } else if (this.cameraMode === 'cockpit-hood') {
      this.cameraMode = 'chase';
      this.mainCamera?.up?.set(0, 1, 0);
    } else if (this.cameraMode === 'chase') {
      this.cameraMode = 'topdown';
    } else {
      this.cameraMode = 'cockpit-driver';
      this.mainCamera?.up?.set(0, 1, 0);
    }
  }
}
